/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: BI Dashboard
 *| (C) Copyright IBM Corp. 2020
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */

define([
	'underscore',
	'../../../lib/@waca/core-client/js/core-client/utils/UniqueId',
	'../../../lib/@waca/upgrades/UpgradeBase',
	'./utils/WidgetUpgradeUtils'
], (_, UniqueId, UpgradeBase, WidgetUpgradeUtils) => {

	const CHART_ID = 'com.ibm.vis.rave2bundlecomposite';
	// The id map with all boolean properties that are needed to change type
	const PROPERTY_TYPE_UPGRADE_MAP = {
		'lines.smooth': {
			newPropId: 'lineWithPoints.interpolate',
			trueValue: 'cardinal',
			falseValue: 'linear',
			defaultValue: 'linear'
		},
		'markers.visible': {
			newPropId: 'lineWithPoints.display',
			trueValue: 'line_points',
			falseValue: 'line',
			defaultValue: 'line_points'
		}
	};
	// The id map with all properties that are needed to change name
	const PROPERTY_NAME_UPGRADE_MAP = {
		'gridLines.visible': ['valueAxis.gridLines.visible', 'itemAxis.gridLines.visible'],
		'valueAxis.title.visible': ['valueAxis.column.title.visible', 'valueAxis.line.title.visible'],
		'valueLabels.visible': ['column.valueLabels.visible', 'lineWithPoints.valueLabels.visible'],
		'valueLabels.color': ['column.valueLabels.color', 'lineWithPoints.valueLabels.color'],
		'valueLabels.font': ['column.valueLabels.font', 'lineWithPoints.valueLabels.font'],
		'valueAxis.ticks.labels.visible': ['valueAxis.column.ticks.labels.visible', 'valueAxis.line.ticks.labels.visible'],
		'valueAxis.ticks.labels.color': ['valueAxis.column.ticks.labels.color', 'valueAxis.line.ticks.labels.color'],
		'valueAxis.ticks.labels.font': ['valueAxis.column.ticks.labels.font', 'valueAxis.line.ticks.labels.font'],
		'valueAxis.ticks.visible': ['valueAxis.column.ticks.visible', 'valueAxis.line.ticks.visible'],
		'valueAxis.ticks.color': ['valueAxis.column.ticks.color', 'valueAxis.line.ticks.color'],
		'colorPalette': ['colorPalette_column', 'colorPalette_line'],
		'defaultPaletteIndex': ['colorPalette_column_defaultIndex'],
		'compositeLineColor': ['colorPalette_line_defaultIndex']
	};

	class CompositeChartV2Upgrade extends UpgradeBase {

		constructor() {
			super();

			this.VERSION = 1803;
		}

		/**
		 * Perform upgrade
		 *
		 * @param {object} spec - spec to perform upgrade on
		 *
		 * @return {Promise} Promise to be resolved when upgrade performed
		 */
		up(spec) {
			if (!spec) {
				return Promise.resolve(spec);
			}

			if (!WidgetUpgradeUtils.specHasWidgets(spec)) {
				return Promise.resolve(spec);
			}

			_.each(spec.widgets, model => {
				if (WidgetUpgradeUtils.isLiveWidgetModel(model)) {
					if (!model.properties) {
						model.properties = [];
					}
					if (model.visId === CHART_ID) {
						Object.keys(PROPERTY_TYPE_UPGRADE_MAP)
							.forEach(oldPropId => this._upgradeBooleanProperty({ ...{ model: model, oldPropId: oldPropId }, ...PROPERTY_TYPE_UPGRADE_MAP[oldPropId] }));

						Object.keys(PROPERTY_NAME_UPGRADE_MAP)
							.forEach(oldPropId => this._upgradePropertyName(model, oldPropId, PROPERTY_NAME_UPGRADE_MAP[oldPropId]));

						this._splitDataView(model);
					}
				}
			});

			return Promise.resolve(spec);
		}

		// Downgrades are not available in CA
		down(spec) {
			return Promise.resolve(spec);
		}

		/**
		 * Upgrade a boolean type property to a non-boolean type property.
		 *
		 * @param {object} options.model - model to perform upgrade on
		 * @param {string} options.oldPropId -the deprecated prop id
		 * @param {string} options.newPropId - the new prop id
		 * @param {any} options.trueValue - value to set for the new prop when the deprecated value is set to true
		 * @param {any} options.falseValue - value to set for the new prop when the deprecated value is set to false
		 * @param {any} options.defaultValue - value to set for the new prop when the deprecated value is undefined
		 */
		_upgradeBooleanProperty(options) {
			const { model, oldPropId, newPropId, trueValue, falseValue, defaultValue } = options;
			const deprecatedProp = WidgetUpgradeUtils.findProperty(oldPropId, model.properties);
			if (!deprecatedProp) {
				return;
			}
			model.properties = _.filter(model.properties, prop => prop.id !== oldPropId);
			if (deprecatedProp.value && defaultValue !== trueValue) {
				model.properties.push({
					id: newPropId,
					value: trueValue
				});
			} else if (!deprecatedProp.value && defaultValue !== falseValue) {
				model.properties.push({
					id: newPropId,
					value: falseValue
				});
			}
		}

		/**
		 * Upgrade a old property id to a new one(s) with the same property value.
		 *
		 * @param {object} model - model to perform upgrade on
		 * @param {string} oldPropId -the deprecated prop id
		 * @param {string} newPropIds - the list of new prop ids
		 */
		_upgradePropertyName(model, oldId, newIds) {
			const deprecatedProp = WidgetUpgradeUtils.findProperty(oldId, model.properties);
			if (deprecatedProp && !_.isUndefined(deprecatedProp.value)) {
				model.properties = _.filter(model.properties, prop => prop.id !== oldId);
				newIds.forEach(id => {
					model.properties.push({
						id: id,
						value: deprecatedProp.value
					});
				});
			}
		}

		_splitDataView(model) {
			if(!model.data || _.isEmpty(model.data.dataViews)) {
				return;
			}

			const dataViews = model.data && model.data.dataViews;
			const columnDataView = dataViews[0];
			const slots = model.slotmapping && model.slotmapping.slots;
			const lineValueSlot = slots.find((slot) => slot.name === 'lineValue');

			slots.forEach((slot) => {
				delete slot.layerId;
			});

			if(lineValueSlot) {
				const lineValueDataItemId = lineValueSlot.dataItems[0];
				const lineValueDataItemIndex = columnDataView.dataItems.findIndex(dataItem => dataItem.id === lineValueDataItemId);
				const lineValueDataItem = columnDataView.dataItems.splice(lineValueDataItemIndex, 1);

				dataViews.push({
					modelRef: columnDataView.modelRef,
					dataItems: lineValueDataItem,
					id: UniqueId.get('model')
				});
			}
		}
	}

	return new CompositeChartV2Upgrade();
});
